import React from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, FileText, Download, Upload, CheckCircle2, XCircle } from 'lucide-react';
import { publicSubscriptionApi } from '../../lib/api';
import { resolveLogoUrl } from '../../lib/resolveLogoUrl';
import { SignatureDevisEnLigne, ContexteSignature } from './SignatureDevisEnLigne';

interface ContractInfo {
    company_name: string;
    logo_url: string | null;
    brand_color: string;
    label: string;
    montant: string;
    has_document: boolean;
    etat: 'en_attente' | 'recu' | 'decline' | 'clos';
    received_at: string | null;
    lost_reason: string;
    // Signature en ligne (2026-09-26) : même règle que le devis, null tant qu'il n'y a pas de PDF à signer.
    signature?: (ContexteSignature & { signe_en_ligne: boolean }) | null;
}

const fmtDate = (d?: string | null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return ''; }
};

/**
 * Page publique de signature du contrat d'abonnement — même gabarit que PrestationDevisSignaturePage.tsx
 * (devis, Suivi de prestation), et même règle depuis le 2026-09-26 (demande d'Emeni) : signature
 * électronique en ligne (code à 6 chiffres par email, PDF scellé avec certificat), pas un simple dépôt de
 * fichier. Le dépôt manuel (photo/scan) reste possible en second recours, comme pour le devis.
 * L'établissement valide ensuite manuellement (bouton « Valider » du module Abonnements) avant que
 * l'abonnement ne passe « Actif » — la signature en ligne ne change pas cette étape.
 */
export const ContractSignaturePage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [info, setInfo] = React.useState<ContractInfo | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [erreur, setErreur] = React.useState('');
    const [fichier, setFichier] = React.useState<File | null>(null);
    const [envoi, setEnvoi] = React.useState(false);
    const [etat, setEtat] = React.useState<ContractInfo['etat'] | null>(null);
    const [refusOuvert, setRefusOuvert] = React.useState(false);
    const [motif, setMotif] = React.useState('');
    const [depotOuvert, setDepotOuvert] = React.useState(false);
    const [signeEnLigne, setSigneEnLigne] = React.useState(false);

    React.useEffect(() => {
        if (!token) return;
        publicSubscriptionApi.getInfo(token)
            .then((d: any) => { setInfo(d); setEtat(d?.etat || null); })
            .catch((e: any) => setErreur(e?.message || 'Lien invalide ou expiré.'))
            .finally(() => setLoading(false));
    }, [token]);

    const deposer = async () => {
        if (!token || !fichier) return;
        setEnvoi(true);
        setErreur('');
        try {
            const data = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(String(reader.result || ''));
                reader.onerror = () => reject(new Error('Lecture du fichier impossible.'));
                reader.readAsDataURL(fichier);
            });
            await publicSubscriptionApi.uploadSigned(token, { file_name: fichier.name, mime_type: fichier.type || 'application/pdf', data });
            setEtat('recu');
        } catch (e: any) {
            setErreur(e?.message || "Votre document n'a pas pu être envoyé.");
        } finally {
            setEnvoi(false);
        }
    };

    const decliner = async () => {
        if (!token || !motif.trim()) return;
        setEnvoi(true);
        setErreur('');
        try {
            await publicSubscriptionApi.decline(token, motif.trim());
            setEtat('decline');
        } catch (e: any) {
            setErreur(e?.message || "Votre réponse n'a pas pu être enregistrée.");
        } finally {
            setEnvoi(false);
        }
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center bg-surface-app"><Loader2 size={32} className="animate-spin text-content-muted" /></div>;
    }

    if (!info) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-surface-app p-4 text-center">
                <p className="text-content font-bold mb-1">Lien invalide</p>
                <p className="text-content-muted text-sm">{erreur || "Ce lien n'est plus valide."}</p>
            </div>
        );
    }

    const logo = resolveLogoUrl(info.logo_url);
    const champ = 'w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500';

    return (
        <div className="min-h-screen bg-surface-app py-10 px-4">
            <div className="max-w-lg mx-auto space-y-5">
                <div className="text-center space-y-2">
                    {logo && <img src={logo} alt={info.company_name} className="h-14 mx-auto object-contain" />}
                    <h1 className="text-xl font-black text-content">{info.company_name}</h1>
                </div>

                <div className="bg-surface border border-border rounded-2xl p-5 space-y-4">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-content-muted">Contrat d'abonnement</p>
                        <p className="text-lg font-bold text-content mt-0.5">{info.label}</p>
                        {info.montant && <p className="text-sm text-content-muted">Tarif : <span className="font-bold text-content">{info.montant}</span></p>}
                    </div>

                    {info.has_document && (
                        <a href={`/api/contrat/${token}/document`} target="_blank" rel="noreferrer"
                            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-border hover:border-brand-500/50 text-sm font-bold text-content transition-colors">
                            <Download size={15} /> Télécharger le contrat
                        </a>
                    )}

                    {etat === 'recu' ? (
                        <div className="space-y-3">
                            <div className="flex items-start gap-2.5 p-3 rounded-xl bg-green-500/10 border border-green-500/30">
                                <CheckCircle2 size={18} className="text-green-500 shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm font-bold text-content">
                                        {signeEnLigne || info.signature?.signe_en_ligne ? 'Votre contrat est signé. C\'est fait !' : 'Votre contrat signé nous est bien parvenu.'}
                                    </p>
                                    <p className="text-xs text-content-muted mt-0.5">
                                        {info.received_at ? `Reçu le ${fmtDate(info.received_at)}. ` : ''}
                                        {signeEnLigne || info.signature?.signe_en_ligne
                                            ? 'Une copie signée, avec son certificat, vient de vous être envoyée par email.'
                                            : 'Nous revenons vers vous très vite pour confirmer l\'activation. Rien d\'autre à faire de votre côté.'}
                                    </p>
                                </div>
                            </div>
                            {(signeEnLigne || info.signature?.signe_en_ligne) && (
                                <a href={`/api/contrat/${token}/signed-document`} target="_blank" rel="noreferrer"
                                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-border hover:border-brand-500/50 text-sm font-bold text-content transition-colors">
                                    <Download size={15} /> Télécharger mon contrat signé
                                </a>
                            )}
                        </div>
                    ) : etat === 'decline' ? (
                        <div className="flex items-start gap-2.5 p-3 rounded-xl bg-surface-soft border border-border">
                            <XCircle size={18} className="text-content-muted shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-bold text-content">Nous avons bien noté que vous ne donniez pas suite.</p>
                                <p className="text-xs text-content-muted mt-0.5">Merci de nous l'avoir dit — et à une prochaine fois.</p>
                            </div>
                        </div>
                    ) : etat === 'clos' ? (
                        <p className="text-sm text-content-muted">
                            Ce contrat n'attend plus de réponse de votre part. Contactez votre interlocuteur si vous avez une question.
                        </p>
                    ) : (
                        <>
                            {info.signature && (
                                <SignatureDevisEnLigne token={token || ''} ctx={info.signature} api={publicSubscriptionApi} documentLabel="contrat"
                                    onSigned={(recu) => { setSigneEnLigne(true); setEtat('recu'); setInfo({ ...info, received_at: recu }); }} />
                            )}

                            {info.signature && !depotOuvert ? (
                                <button type="button" onClick={() => setDepotOuvert(true)} className="w-full text-xs text-content-muted hover:text-content underline pt-1">
                                    Je préfère imprimer, signer à la main et déposer mon exemplaire
                                </button>
                            ) : (
                            <div className="space-y-2 pt-1">
                                <p className="text-xs font-bold text-content">Déposer votre exemplaire signé</p>
                                <label className="block cursor-pointer">
                                    <input type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => setFichier(e.target.files?.[0] || null)} />
                                    <div className={`border-2 border-dashed rounded-xl p-5 flex flex-col items-center justify-center gap-1 transition-colors ${fichier ? 'border-brand-500/60 bg-brand-500/5' : 'border-border hover:border-brand-500/40'}`}>
                                        {fichier ? (
                                            <>
                                                <FileText size={22} className="text-brand-600 dark:text-brand-400" />
                                                <p className="text-xs font-bold text-content">{fichier.name}</p>
                                                <p className="text-[10px] text-content-muted">{(fichier.size / 1024).toFixed(0)} Ko</p>
                                            </>
                                        ) : (
                                            <>
                                                <Upload size={22} className="text-content-muted" />
                                                <p className="text-xs font-bold text-content">Choisir le fichier</p>
                                                <p className="text-[10px] text-content-muted">PDF ou photo — 10 Mo maximum</p>
                                            </>
                                        )}
                                    </div>
                                </label>
                                <button type="button" onClick={deposer} disabled={!fichier || envoi}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-black disabled:opacity-50">
                                    {envoi ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Envoyer mon contrat signé
                                </button>
                            </div>
                            )}

                            <div className="pt-2 border-t border-border">
                                {!refusOuvert ? (
                                    <button type="button" onClick={() => setRefusOuvert(true)} className="w-full text-xs text-content-muted hover:text-content underline">
                                        Je ne donne pas suite
                                    </button>
                                ) : (
                                    <div className="space-y-2">
                                        <p className="text-xs font-bold text-content">Pouvez-vous nous dire pourquoi ?</p>
                                        <p className="text-[11px] text-content-muted">Une phrase suffit. Cela nous aide vraiment à nous améliorer.</p>
                                        <textarea value={motif} onChange={(e) => setMotif(e.target.value)} rows={3} placeholder="Budget, engagement trop long, autre prestataire..." className={`${champ} resize-none`} />
                                        <div className="flex gap-2">
                                            <button type="button" onClick={() => { setRefusOuvert(false); setMotif(''); }} disabled={envoi}
                                                className="flex-1 px-3 py-2 rounded-xl border border-border text-xs font-bold text-content-muted hover:text-content">
                                                Annuler
                                            </button>
                                            <button type="button" onClick={decliner} disabled={!motif.trim() || envoi}
                                                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border border-border text-xs font-bold text-content hover:bg-surface-soft disabled:opacity-50">
                                                {envoi ? <Loader2 size={13} className="animate-spin" /> : null} Envoyer ma réponse
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {erreur && <p className="text-xs text-red-500 font-semibold">{erreur}</p>}
                </div>

                <p className="text-[10px] text-content-muted text-center">Ce lien vous est personnel. Ne le transmettez pas.</p>
            </div>
        </div>
    );
};
